import React, { useState } from 'react';
import {
  useListAiActivityQuery,
  useConfirmAiActivityMutation,
} from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
};

const ACTIVITY_KEY = 'claude_projects_video';
const VIDEO_URL = 'https://www.youtube.com/watch?v=z8PWVAAOTL8';
const VIDEO_EMBED = 'https://www.youtube.com/embed/z8PWVAAOTL8';

export default function ClaudeProjectsVideo() {
  const isMobile = useIsMobile();
  const { data: activities, isLoading } = useListAiActivityQuery();
  const [confirmActivity, { isLoading: isConfirming }] = useConfirmAiActivityMutation();
  const [error, setError] = useState(null);

  const activity = (activities || []).find(a => a.activity_key === ACTIVITY_KEY);
  const isDone = activity?.status === 'completed';

  const handleConfirm = async () => {
    setError(null);
    try {
      await confirmActivity({ activity_key: ACTIVITY_KEY }).unwrap();
    } catch (err) {
      setError(err?.data?.detail || 'Could not record confirmation. Please try again.');
    }
  };

  const pagePad = isMobile ? '1rem' : '1.5rem';
  const bannerPad = isMobile ? '1.25rem 1rem' : '1.75rem 2rem';
  const cardPad = isMobile ? '1rem' : '1.25rem';

  if (isLoading) {
    return (
      <div style={{ background: brand.softGray, minHeight: '100vh', padding: pagePad, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner-border" style={{ color: brand.tealBlue }} />
      </div>
    );
  }

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: pagePad }}>

      {/* Header banner — mirrors ClaudeAccess.js for visual continuity */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: bannerPad, marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '0.75rem' : '1rem', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: isMobile ? '0.5rem' : '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-play-btn" style={{ color: brand.goldenrod, fontSize: isMobile ? '1.4rem' : '1.75rem' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: isMobile ? '0.65rem' : '0.75rem', textTransform: 'uppercase', letterSpacing: isMobile ? '1.5px' : '2px', margin: '0 0 4px' }}>
              Enterprise AI Initiative
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.15rem' : '1.75rem', lineHeight: 1.2 }}>Claude Projects: Deep Dive</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
              Watch the walkthrough, then confirm completion below.
            </p>
          </div>
          {isDone && (
            <div style={{
              marginLeft: isMobile ? 0 : 'auto',
              marginTop: isMobile ? '0.25rem' : 0,
              background: brand.green,
              borderRadius: '10px',
              padding: isMobile ? '0.4rem 0.75rem' : '0.75rem 1.25rem',
              textAlign: 'center',
              flexShrink: 0,
              display: isMobile ? 'inline-flex' : 'block',
              alignItems: 'center',
              gap: isMobile ? '0.4rem' : 0,
            }}>
              <i className="bi bi-patch-check-fill" style={{ color: 'white', fontSize: isMobile ? '1rem' : '1.5rem', display: isMobile ? 'inline' : 'block' }} />
              <div style={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', marginTop: isMobile ? 0 : '4px' }}>Confirmed</div>
            </div>
          )}
        </div>
      </div>

      <div className="row g-4 justify-content-center">
        <div className="col-lg-9 mx-auto">

          {/* Video card */}
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: cardPad, marginBottom: isMobile ? '1rem' : '1.25rem' }}>
            <h5 style={{ color: brand.deepBlue, fontWeight: 700, marginBottom: '0.75rem', fontSize: isMobile ? '1.05rem' : '1.25rem' }}>Watch the video</h5>
            <p style={{ color: brand.charcoal, fontSize: isMobile ? '0.875rem' : '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              This deep dive shows how to use Projects in Claude to organize work, share context with the model, and collaborate with teammates. Watch all the way through, then confirm below.
            </p>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px', background: '#000' }}>
              <iframe
                title="Claude Projects: Deep Dive"
                src={VIDEO_EMBED}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p style={{ color: brand.charcoal, fontSize: '0.8rem', marginTop: '0.75rem', marginBottom: 0 }}>
              Trouble loading? <a href={VIDEO_URL} target="_blank" rel="noreferrer" style={{ color: brand.tealBlue }}>Open on YouTube</a>.
            </p>
          </div>

          {/* Confirm card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 12px rgba(10,41,71,0.08)',
            padding: cardPad,
            border: isDone ? `2px solid ${brand.green}` : '2px solid transparent',
          }}>
            <h5 style={{ color: brand.deepBlue, fontWeight: 700, marginBottom: '0.5rem', fontSize: isMobile ? '1.05rem' : '1.25rem' }}>Confirm completion</h5>
            <p style={{ color: brand.charcoal, fontSize: isMobile ? '0.875rem' : '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              By clicking the button below you confirm you watched the full <em>Claude Projects: Deep Dive</em> video.
            </p>
            {isDone ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: brand.green, fontWeight: 700, flexWrap: 'wrap' }}>
                <i className="bi bi-check-circle-fill" style={{ fontSize: '1.25rem' }} />
                <span>
                  Confirmed
                  {activity?.completed_at && (
                    <span style={{ color: brand.charcoal, fontWeight: 500, marginLeft: '0.5rem' }}>
                      on {new Date(activity.completed_at).toLocaleString()}
                    </span>
                  )}
                </span>
              </div>
            ) : (
              <button
                type="button"
                className="btn"
                onClick={handleConfirm}
                disabled={isConfirming}
                style={{
                  background: brand.tealBlue,
                  color: 'white',
                  fontWeight: 700,
                  padding: '0.6rem 1.25rem',
                  borderRadius: '8px',
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                {isConfirming ? 'Confirming…' : 'I watched the video'}
              </button>
            )}
            {error && (
              <div style={{ marginTop: '0.75rem', color: '#b00020', fontSize: '0.875rem' }}>
                {error}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
